import { useState, useEffect } from "react";
import { X, Plus, Trash2, Shuffle } from "lucide-react";
import { Button } from "../ui/button";

const MAX_LEN = 500;

export default function CampaignMessagesModal({ campaign, onClose, onSave }) {
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const initial = campaign?.config?.messages || [];
    setMessages(initial.length > 0 ? [...initial] : [""]);
  }, [campaign]);

  const updateAt = (idx, value) => {
    setMessages((prev) => prev.map((m, i) => (i === idx ? value : m)));
  };

  const removeAt = (idx) => {
    setMessages((prev) => prev.filter((_, i) => i !== idx));
  };

  const addNew = () => {
    setMessages((prev) => [...prev, ""]);
  };

  const handleSave = async () => {
    const cleaned = messages.map((m) => m.trim()).filter((m) => m.length > 0);
    if (cleaned.length === 0) {
      return;
    }
    setSaving(true);
    try {
      await onSave(cleaned);
    } finally {
      setSaving(false);
    }
  };

  const valid = messages.some((m) => m.trim().length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-2xl border shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-card z-10 rounded-t-2xl">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Shuffle className="w-5 h-5 text-purple-500" />
            Mensagens da campanha
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          <p className="text-sm text-muted-foreground mb-4">
            O ChatBot vai <strong>sortear uma mensagem aleatória</strong> desta
            lista cada vez que a campanha disparar. Adicione, edite ou remova
            mensagens para personalizar o tom da sua loja.
          </p>

          <div className="space-y-2 mb-4">
            {messages.map((msg, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <textarea
                  value={msg}
                  onChange={(e) => updateAt(idx, e.target.value)}
                  maxLength={MAX_LEN}
                  placeholder="Ex: Não deixe para amanhã o que você pode comer agora 😋"
                  rows={2}
                  className="flex-1 px-3 py-2 rounded-lg border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-none"
                />
                <button
                  onClick={() => removeAt(idx)}
                  disabled={messages.length === 1}
                  className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Remover"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={addNew}
            className="gap-1.5 w-full"
          >
            <Plus className="w-4 h-4" /> Adicionar mensagem
          </Button>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t bg-muted/20 sticky bottom-0 rounded-b-2xl">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!valid || saving}
            className="gap-1.5 bg-purple-600 hover:bg-purple-700"
          >
            {saving ? "Salvando..." : "Salvar mensagens"}
          </Button>
        </div>
      </div>
    </div>
  );
}
